import { motion } from "framer-motion";
import sfgateLogo from "@/assets/sfgate-logo.svg";
import poetsQuantsLogo from "@/assets/poets-and-quants-logo.png";

interface AwardsBassProps {
    /** Optional statement rendered above the "Winner of Hubspot" line (e.g. men's page). */
    headline?: string;
    /** Splash pages: below md, render the mobile proof block + press marquee. */
    mobileRedesign?: boolean;
}

const AwardsBass = ({ headline, mobileRedesign = false }: AwardsBassProps = {}) => {
    // Static brand-proof facts (same on every page); the mobile marquee only.
    const PROOF_ROWS = [
        { label: "Winner of", value: "HubSpot Next Big AIdea" },
        { label: "Featured in", value: "THE HUSTLE · SFGATE · POETS&QUANTS" },
        { label: "Stylists featured in", value: "ELLE · VOGUE · VANITY FAIR" },
        { label: "Trained at", value: "Parsons · FIT · SCAD" },
    ];

    // Mobile: the desktop proof block (headline, subhead, wordmarks) stacked on
    // the alt ground, plus a continuous press marquee.
    const MARQUEE_CSS = `@keyframes marqueeX{from{transform:translateX(0)}to{transform:translateX(-50%)}}`;
    const mobile = (
        <section className="md:hidden bg-[#F9FAFB] px-6 pt-16 pb-[72px] text-center">
            <style>{MARQUEE_CSS}</style>
            {headline && (
                <h2 className="mb-5 font-playfair text-[34px] font-bold leading-[1.1] text-[#0f172a] [text-wrap:balance]">
                    {headline}
                </h2>
            )}
            <p className="mb-8 text-[12px] font-semibold uppercase leading-[1.7] tracking-[0.15em] text-[#064e3b]/70">
                Winner of Hubspot's Next Big AIdea and featured in
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 opacity-75">
                <span className="text-[22px] font-bold tracking-[-0.02em] text-[#ff5c35]">HubSpot</span>
                <span className="font-playfair text-[20px] font-bold tracking-[0.04em] text-[#022c22]">THE HUSTLE</span>
                <img src={sfgateLogo} alt="SFGate" className="h-6 object-contain" />
                <img src={poetsQuantsLogo} alt="Poets&Quants" className="h-5 object-contain" />
            </div>
            <div className="-mx-6 mt-9 overflow-hidden border-y border-[#e7e5e4] py-3.5">
                <div className="flex w-max animate-[marqueeX_32s_linear_infinite] motion-reduce:animate-none">
                    {[0, 1].map((dup) => (
                        <div key={dup} aria-hidden={dup === 1} className="flex shrink-0 items-baseline gap-11 pr-11">
                            {PROOF_ROWS.map((row) => (
                                <div key={row.label} className="flex shrink-0 items-baseline gap-2">
                                    <span className="text-[11px] uppercase tracking-[0.1em] text-[#a8a29e]">{row.label}</span>
                                    <span className="whitespace-nowrap font-playfair text-[16px] italic text-[#0f172a]">{row.value}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    const desktop = (
        <section className="bg-[#FFF7F0] py-20 sm:py-24 relative overflow-hidden">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-16 lg:px-[140px] relative z-10">
                <div className="flex flex-col items-center gap-8">
                    {headline && (
                        <h2 className="text-[#0f172a] text-2xl sm:text-3xl md:text-[40px] font-bold font-playfair text-center tracking-tight leading-[1.12] max-w-3xl">
                            {headline}
                        </h2>
                    )}
                    <h3 className="text-[#064e3b]/70 text-sm sm:text-base font-medium font-jakarta text-center uppercase tracking-wider max-w-2xl leading-relaxed">
                        Winner of Hubspot's Next Big AIdea and featured in
                    </h3>
                    <div className="flex flex-wrap justify-center items-center gap-12 sm:gap-16 md:gap-20 opacity-70 hover:opacity-100 transition-all duration-500">
                        <motion.div whileHover={{ scale: 1.05 }} className="flex items-center justify-center">
                            <span className="text-xl sm:text-2xl font-bold text-[#ff5c35]">HubSpot</span>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} className="flex items-center justify-center">
                            <span className="text-xl sm:text-2xl font-bold text-[#022c22] font-playfair">THE HUSTLE</span>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} className="flex items-center justify-center">
                            <img src={sfgateLogo} alt="SFGate" className="h-6 sm:h-7 object-contain" />
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} className="flex items-center justify-center">
                            <img src={poetsQuantsLogo} alt="Poets&Quants" className="h-5 sm:h-6 object-contain" />
                        </motion.div>
                    </div>
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[40px] sm:h-[60px] pointer-events-none">
                <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 60" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,60 L1440,60 L1440,0 C1440,0 1080,40 720,40 C360,40 0,0 0,0 Z" fill="#F9FAFB" />
                </svg>
            </div>
        </section>
    );

    if (!mobileRedesign) return desktop;

    return (
        <>
            {mobile}
            <div className="hidden md:block">{desktop}</div>
        </>
    );
};

export default AwardsBass;